import { useFetch, useSubmit } from "@hyper-fetch/react";
import { useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { surface } from "@/components/layouts/shells/shell-shared";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { BookingMarkdown } from "@/features/booking/modules/markdown/markdown";
import { SellerNotice } from "@/features/legal/components/seller-notice";
import { offerPrice } from "@/features/payments/utils/format";
import { cn } from "@/lib/utils";
import { signInHref } from "@/lib/locale";
import { m } from "@/paraglide/messages.js";

export type Offer = { id: string; kind: string; title: string; description: string; packSessions: number | null; sessionsPerPeriod: number | null } & Parameters<
  typeof offerPrice
>[0];

const detailOf = (offer: Offer) => {
  if (offer.kind === "entry_pack" && offer.packSessions) return m.offers_pack_sessions({ count: String(offer.packSessions) });
  if (offer.kind === "client_subscription" && offer.sessionsPerPeriod)
    return m.offers_sessions_per_period({ count: String(offer.sessionsPerPeriod) });
  if (offer.kind === "training_plan" || offer.kind === "diet_plan") return m.offers_made_to_order();
  return null;
};

/**
 * What a trainer sells besides sessions: entry packs, made-to-order plans and recurring coaching,
 * each with a button that goes straight to Stripe Checkout. Buying makes the person the trainer's
 * client first (for a trainer who takes clients on their own); someone signed out is sent to sign in
 * and back.
 */
export function TrainerOffers({
  organizationId,
  initial,
  onCheckout = (url) => window.location.assign(url),
  onSignIn = () => window.location.assign(signInHref()),
}: {
  organizationId: string;
  /** What the server read with the page, shown until the browser's own answer comes. */
  initial?: Offer[];
  onCheckout?: (url: string) => void;
  onSignIn?: () => void;
}) {
  const { data: fetched } = useFetch(sdk.publicOffers.$get.setQueryParams({ organizationId }));
  const data = fetched ?? initial;
  const join = useSubmit(sdk.bookingClient.joinPublic.$post);
  const buy = useSubmit(sdk.me.payments.offers.$offerId.checkout.$post);
  const subscribe = useSubmit(sdk.me.payments.offers.$offerId.subscribe.$post);
  const [busy, setBusy] = useState<string | null>(null);

  if (!data || data.length === 0) return null;

  const start = async (offer: Offer) => {
    setBusy(offer.id);
    const joined = await join.submit({ payload: { organizationId } });
    if (joined.status === 401) {
      setBusy(null);
      onSignIn();
      return;
    }
    const request = offer.kind === "client_subscription" ? subscribe : buy;
    const { data: started, error } = await request.submit({ params: { offerId: offer.id }, queryParams: { organizationId } });
    if (error || !started?.url) {
      setBusy(null);
      toast.error(error?.message ?? m.offers_buy_failed());
      return;
    }
    onCheckout(started.url);
  };

  return (
    <section className="grid gap-4" aria-labelledby="trainer-offers-title" data-slot="trainer-offers">
      <h2 id="trainer-offers-title" className="text-xl font-semibold tracking-tight">
        {m.offers_section_title()}
      </h2>
      <ul className="grid gap-4 md:grid-cols-2">
        {data.map((offer) => {
          const detail = detailOf(offer);
          return (
            <li key={offer.id} className={cn(surface, "grid gap-3 p-5")}>
              <div className="grid gap-1">
                <h3 className="font-semibold">{offer.title}</h3>
                {detail ? <p className="text-sm text-muted-foreground">{detail}</p> : null}
                {offer.description ? <BookingMarkdown source={offer.description} clampLines={4} /> : null}
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-lg font-semibold tabular-nums">{offerPrice(offer)}</span>
                <Button type="button" onClick={() => void start(offer)} disabled={busy !== null}>
                  {busy === offer.id ? <Spinner /> : null}
                  {offer.kind === "client_subscription" ? m.offers_subscribe() : m.offers_buy()}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <SellerNotice organizationId={organizationId} />
    </section>
  );
}
